import {
  HStack,
  Text,
  Box,
  Button,
  useToast,
  Image,
  Spinner,
  Switch,
  Icon,
} from "@chakra-ui/react";
import { Trash, CaretDoubleRight, CalendarPlus } from "phosphor-react";
import React, { useState, useEffect } from "react";
import { COLORS } from "../../Helper/Colors";
import TableStyle from "../../Components/Common/TableStyle";
import ProfileImage from "../../Assets/profile_placeholder.jpg";
import { DeleteModal, Pagination, Search } from "../../Components/Common/UI";
import TableV6 from "../../Components/Common/TableV6";
import Api from "../../Helper/ApiHandler";
import { useQuery } from "react-query";
import { useDebounce } from "../../Helper/Hooks/useDebounce";
import { useNavigate, useLocation } from "react-router-dom";

const UserTable = () => {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const api = new Api();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [params, setParams] = useState({
    pageNo: 1,
    pageSize: 25,
    order: "DESC",
    sortBy: "id",
  });
  const [changeId, setChangeId] = useState(null);
  const [deleteBtnLoading, setDeleteBtnLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const debouncedSearchTerm = useDebounce(search, 1000);
  let addDefaultSrc = (ev) => {
    ev.target.src = ProfileImage;
  };

  const fetchUser = async ({ queryKey }) => {
    const userList = await api.post(`${"user/list"}`, {
      page: state?.count ? state.pageNo : queryKey[2].pageNo,
      search: state?.count ? state.search : queryKey[1],
      order: state?.count ? state.order : queryKey[2].order,
      sortBy: state?.count ? state.sortBy : queryKey[2].sortBy,
    });
    setData(userList?.data?.rows);
    console.log("userList: ", userList);
    return userList;
  };

  const {
    isLoading,
    data: userData,
    refetch,
  } = useQuery(["userLists", debouncedSearchTerm, params], fetchUser);

  const handleSwitchChange = async (value) => {
    try {
      setChangeId(value._id);
      await api.put("user/change-status", {
        userId: value._id,
      });
      await refetch();
      setChangeId(null);
      return;
    } catch (error) {
      toast({
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    }
  };

  const handlePageChange = (page) => {
    setParams((prev) => ({ ...prev, pageNo: page.selected + 1 }));
  };

  const handleSortBy = (sortBy) => {
    setParams((prev) => ({
      ...prev,
      sortBy: sortBy[0]?.id || "id",
      order: sortBy[0]?.desc ? "DESC" : "ASC",
    }));
  };

  const handleDelete = async (type) => {
    if (type === "CANCEL") {
      setIsDeleteModalOpen(false);
      return;
    }
    try {
      setDeleteBtnLoading(true);
      await api.put("user/delete", { userId: String(deleteId) });
      setIsDeleteModalOpen(false);
      setDeleteBtnLoading(false);
      setDeleteId(null);
      toast({
        description: "User has been deleted.",
        status: "success",
        duration: 3000,
        position: "top",
      });

      if (params.pageNo > 1 && userData?.data?.length === 1) {
        setParams({ ...params, pageNo: params.pageNo - 1 });
      } else {
        await refetch();
      }
    } catch (error) {
      toast({
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
      setDeleteBtnLoading(false);
    }
  };

  const columns = [
    {
      Header: () => (
        <Box>
          <Text>Image</Text>
        </Box>
      ),
      accessor: "profilePhotoUrl",
      Cell: (row) => (
        <Box display={"flex"} justifyContent={"center"}>
          <Image
            onError={addDefaultSrc}
            borderRadius="full"
            boxSize="45px"
            src={row.value == null ? ProfileImage : row.value}
            alt="Img"
          />
        </Box>
      ),
      sortable: false,
    },
    {
      Header: () => (
        <Box>
          <Text marginLeft={"5px"}>Name</Text>
        </Box>
      ),
      accessor: "firstName",
      Cell: (row) => (
        <Text textAlign={"center"}>
          {row.original.firstName + " " + row.original.lastName}
        </Text>
      ),
      sortable: false,
    },
    {
      Header: () => (
        <Box>
          <Text marginLeft={"5px"}>UserName</Text>
        </Box>
      ),
      accessor: "userName",
      Cell: (row) => (
        <Box textAlign={"center"} textTransform={"none"}>
          {row.original.userName}
        </Box>
      ),
    },
    {
      Header: () => (
        <Box>
          <Text marginLeft={"5px"}>Email</Text>
        </Box>
      ),
      accessor: "email",
      Cell: (row) => (
        <Text textAlign={"center"} textTransform={"lowercase"}>
          {row.value}
        </Text>
      ),
    },
    {
      Header: () => (
        <Text textAlign={"center"} marginLeft={"15px"}>
          Status
        </Text>
      ),
      accessor: "isActive",
      Cell: (row) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {changeId !== row.original._id ? (
            <Switch
              isChecked={row.value}
              onChange={() => handleSwitchChange(row.original)}
            />
          ) : (
            <Spinner color={COLORS.brand} size={"sm"} />
          )}
        </div>
      ),
      sortable: false,
      width: 100,
    },
    {
      Header: () => (
        <Box>
          <Text borderColor={"white"} boxShadow="none" marginLeft={"10px"}>
            Action
          </Text>
        </Box>
      ),
      accessor: "_id",
      Cell: (row) => (
        <>
          <HStack
            alignItems={"center"}
            justifyContent="center"
            textAlign={"center"}
          >
            <Button
              backgroundColor={COLORS.brand}
              color={"white"}
              title="Delete"
              _hover={{ backgroundColor: COLORS.lightPink }}
              onClick={() => {
                setDeleteId(row.original._id);
                setIsDeleteModalOpen(true);
              }}
            >
              {<Trash size={18} color={"white"} />}
            </Button>

            <Button
              backgroundColor={COLORS.brand}
              color={"white"}
              title="View Profile"
              transition="all .8s ease"
              mx=".3rem"
              cursor="pointer"
              _hover={{ transform: "translate(10%)" }}
              onClick={() => {
                navigate(`/user/${row.value}`, {
                  state: {
                    ...params,
                    search: search,
                  },
                });
              }}
            >
              <Icon
                as={CaretDoubleRight}
                w="20px"
                h="20px"
                fontSize="xl"
                pt="4px"
              />
            </Button>
          </HStack>
        </>
      ),
      sortable: false,
    },
  ];

  return (
    <>
      <HStack
        display={"flex"}
        justifyContent={"space-between"}
        paddingBottom={"10px"}
      >
        <Search
          search={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setParams({ ...params, pageNo: 1 });
          }}
          resetSearch={() => setSearch("")}
        />
      </HStack>
      <TableStyle version={6}>
        <TableV6
          columns={columns}
          data={data}
          handleSortBy={handleSortBy}
          isLoading={isLoading}
          key={"master-rep-table"}
        />
        {data.length ? (
          <Pagination
            key={"UserPagination"}
            handlePageChange={handlePageChange}
            pageSize={10}
            total={userData?.data?.count || 0}
            currentPage={params.pageNo}
          />
        ) : null}
      </TableStyle>
      <DeleteModal
        heading={"Delete User"}
        isOpen={isDeleteModalOpen}
        onCancel={() => handleDelete("CANCEL")}
        onDelete={() => handleDelete("DELETE")}
        deleteBtnLoading={deleteBtnLoading}
      />
    </>
  );
};

export default UserTable;
